import type { Shop, Employee, BusinessProfile, DeliveryZone } from '@@partner:types:app'
import type { OrdersFiltersType } from '@components/order/table/OrdersFilter'
import type { FormatLabelFn, CommonStatusUILabel } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'

import { SHOP } from '@hive/status'
import { formatOrderStatusLabel } from '@@partner:helpers/formatters/order'

import { ORDER_CREATE_TYPE_OPERATOR } from '@@partner:config/order-create-types'
import {
  ORDER_STATUS_INITIAL,
  ORDER_STATUS_PLACED,
  ORDER_STATUS_CANCELED,
  ORDER_STATUS_PAID,
  ORDER_STATUS_WAITING_FOR_PAYMENT,
  ORDER_STATUS_ASSEMBLY_IN_PROGRESS,
  ORDER_STATUS_ASSEMBLY_COMPLETE,
  ORDER_STATUS_ACCEPTED_BY_COURIER,
  ORDER_STATUS_WAITING_FOR_COURIER,
  ORDER_STATUS_DEVIVERY_IN_PROGRESS,
  ORDER_STATUS_DELIVERED,
  ORDER_STATUS_COMPLETED
} from '@@partner:config/order-status-codes'

interface OrderStatusUISettings extends CommonStatusUILabel {
  value: number
}

/*
  Check is order editable by status
*/

export function isTaken (status: number): boolean {
  return ![ ORDER_STATUS_INITIAL, ORDER_STATUS_PLACED ].includes(status)
}

export function isEditable (status: number): boolean {
  return [ ORDER_STATUS_INITIAL ].includes(status)
}

/*
  Order statuses for filtering
*/

export const getSearchableOrderStatuses: FormatLabelFn<
  void,
  OrderStatusUISettings[]
> = () => {
  const SEARCHABLE_STATUSES = [
    ORDER_STATUS_INITIAL,
    ORDER_STATUS_PLACED,
    ORDER_STATUS_CANCELED,
    ORDER_STATUS_PAID,
    ORDER_STATUS_WAITING_FOR_PAYMENT,
    ORDER_STATUS_ASSEMBLY_IN_PROGRESS,
    ORDER_STATUS_ASSEMBLY_COMPLETE,
    ORDER_STATUS_ACCEPTED_BY_COURIER,
    ORDER_STATUS_WAITING_FOR_COURIER,
    ORDER_STATUS_DEVIVERY_IN_PROGRESS,
    ORDER_STATUS_DELIVERED,
    ORDER_STATUS_COMPLETED
  ]

  return SEARCHABLE_STATUSES.map((statusCode) => {
    const statusConfig = formatOrderStatusLabel(statusCode)

    return {
      value: statusCode,
      label: statusConfig.label,
      color: statusConfig.color
    }
  })
}

/*
  Shops for filtering
*/

export function getSearchableShops (shops: Shop[]) {
  if (!shops) {
    return []
  }
  return shops
    .filter((shop) => shop.status !== SHOP.ARCHIVED)
    .map((shop) => {
      return {
        value: shop.id,
        label: shop.name + ' ' + shop.address
      }
    })
}

export function getSearchableZones (zones: DeliveryZone[]) {
  if (!zones) {
    return []
  }
  return zones.map((zone) => {
    return {
      value: zone.id,
      label: zone.name
    }
  })
}


/*
  Employee for filtering
*/

export function getSearchableEmployee (employee: Employee[]) {
  if (!employee) {
    return []
  }

  return employee.map((employee) => {
    return {
      value: employee.id,
      label: employee.fullName,
      color: employee.status === 1 ? 'green' : 'red'
    }
  })
}


export const getSearchableBusinessProfile = (buinessProfiles?: BusinessProfile[]) => {
  if (!buinessProfiles) {
    return []
  }

  return buinessProfiles.map(businessProfile => {
    return {
      value: businessProfile.id,
      label: businessProfile.name,
      color: 'green'
    }
  })
}

/*
  Get order step by status for orders page
*/

export const getStepFromOrderStatus = (status: number): number => {
  switch (status) {
  case ORDER_STATUS_PLACED:
    return 0
  case ORDER_STATUS_ASSEMBLY_IN_PROGRESS:
    return 1
  case ORDER_STATUS_ASSEMBLY_COMPLETE:
    return 1
  case ORDER_STATUS_ACCEPTED_BY_COURIER:
    return 2
  case ORDER_STATUS_WAITING_FOR_COURIER:
    return 2
  case ORDER_STATUS_DEVIVERY_IN_PROGRESS:
    return 2
  case ORDER_STATUS_DELIVERED:
    return 3
  case ORDER_STATUS_COMPLETED:
    return 3
  default:
    return 0
  }
}

/*
  Get is order can be canceled by status
*/

export const isOrderCancellable = (orderStatus: number): boolean => {
  switch (orderStatus) {
  case ORDER_STATUS_PLACED:
    return true
  default:
    return false
  }
}

/*
  Format weight number to formatted string
*/

export const formatOrderWeight = (weight = 0): string => {
  if (weight >= 1000) {
    return `${(weight / 1000).toFixed(2)} кг.`
  }
  return `${weight.toFixed(2)} гр.`
}

export const makeDefaultFilter = (field: string) => ({
  fields: [ field ],
  op: 'unwrap'
})

export const prepareOrdersFilter = (filters: OrdersFiltersType) => {
  const addressFilter = filters.address
    ? {
      fields: [ 'address' ],
      op: 'eq',
      value: filters.address
    }
    : makeDefaultFilter('address')

  const idFilter =
    filters.ids && typeof filters.ids === 'string' && filters.ids.trim().length > 0
      ? {
        fields: [ 'id' ],
        op: 'contains',
        value: filters.ids.split(/[,\s]+/).filter(id => id.trim() && /\d+/.test(id))
      }
      : makeDefaultFilter('id')

  const phoneFilter =
    filters.phone !== ''
      ? {
        fields: [ 'customer_phone' ],
        op: 'eq',
        value: filters.phone
      }
      : makeDefaultFilter('customer_phone')

  // TODO: WTF is that? Make helper functions to simplify that piece of code
  const placedAtFilter =
    filters.from || filters.to
      ? filters.from && filters.to
        ? {
          fields: [ 'placedAt' ],
          op: 'between',
          value: [ filters.from, filters.to ]
        }
        : filters.from && !filters.to
          ? {
            fields: [ 'placedAt' ],
            op: 'gte',
            value: [ filters.from ]
          }
          : !filters.from && filters.to
            ? {
              fields: [ 'placedAt' ],
              op: 'lte',
              value: [ filters.to ]
            }
            : makeDefaultFilter('placedAt')
      : makeDefaultFilter('placedAt')

  const statusFilter =
    filters.statuses.length > 0
      ? {
        fields: [ 'status' ],
        op: 'contains',
        value: filters.statuses
      }
      : makeDefaultFilter('status')

  const shopsFilter =
    filters.shops.length > 0
      ? {
        fields: [ 'shop_id' ],
        op: 'contains',
        value: filters.shops
      }
      : makeDefaultFilter('shop_id')

  const assemblersFilter =
    filters.assemblers.length > 0
      ? {
        fields: [ 'assembler_id' ],
        op: 'contains',
        value: filters.assemblers
      }
      : makeDefaultFilter('assembler_id')

  const couriersFilter =
    filters.couriers.length > 0
      ? {
        fields: [ 'courier_id' ],
        op: 'contains',
        value: filters.couriers
      }
      : makeDefaultFilter('courier_id')

  const businessProfilesFilter =
    filters.business_profile_id.length > 0
      ? {
        fields: [ 'business_profile_id' ],
        op: 'contains',
        value: filters.business_profile_id
      }
      : makeDefaultFilter('business_profile_id')

  const createdByTypeFilter =
    filters.createdByType !== ''
      ? {
        fields: [ 'operator_id' ],
        op:
            filters.createdByType === ORDER_CREATE_TYPE_OPERATOR
              ? 'not_null'
              : 'is_null'
      }
      : makeDefaultFilter('operator_id')

  const paymentTypeFilter =
    filters.paymentType !== ''
      ? {
        fields: [ 'payment_type' ],
        op: 'eq',
        value: [ filters.paymentType ]
      }
      : makeDefaultFilter('payment_type')

  const hasFeedbackFilter = filters.hasFeedback
    ? { fields: [ 'feedback' ], op: 'not_null', value: '' }
    : makeDefaultFilter('feedback')

  const slotStartingAtFilter =
    filters.slotFrom
      ? {
        fields: [ 'slotStartingAt' ],
        op: 'gte',
        value: [ filters.slotFrom ]
      }
      : makeDefaultFilter('slotStartingAt')

  const slotFinishingAtFilter =
    filters.slotTo
      ? {
        fields: [ 'slotFinishingAt' ],
        op: 'lte',
        value: [ filters.slotTo ]
      }
      : makeDefaultFilter('slotFinishingAt')

  return {
    and: [
      addressFilter,
      idFilter,
      phoneFilter,
      placedAtFilter,
      statusFilter,
      shopsFilter,
      assemblersFilter,
      couriersFilter,
      createdByTypeFilter,
      paymentTypeFilter,
      hasFeedbackFilter,
      businessProfilesFilter,
      slotStartingAtFilter,
      slotFinishingAtFilter
    ]
  }
}
